import type { JSX } from '@builder.io/qwik';
import {
  component$,
  Slot,
  useContext,
  useContextProvider,
  useVisibleTask$,
  useSignal,
} from '@builder.io/qwik';
import { AnalyticsContext } from '~/contexts/AnalyticsContext';
import type { PAGE_BUILDER_BLOCK_TYPE } from '~/types/AppData/PageBuilder/PageBuilderBlockTypesType';
import type { AnalyticsEventActionType } from '~/types/AnalyticsType';

type AnalyticsContainerType = {
  component_name: string;
  component_type?: PAGE_BUILDER_BLOCK_TYPE;
  component_placement?: number;
  component_placement_desktop?: number;
  amount_of_articles?: number;
  column_number_of_links?: number;
  fireViewEvent?: boolean;
};

export default component$<AnalyticsContainerType>(
  ({
    component_name,
    component_type,
    component_placement,
    component_placement_desktop,
    amount_of_articles,
    column_number_of_links,
    fireViewEvent = false,
  }): JSX.Element => {
    const analytics = useContext(AnalyticsContext);
    const prevId =
      analytics.event.component_id !== undefined
        ? analytics.event.component_id
        : '';
    let newId = prevId;

    if (prevId && component_name) {
      newId = `${prevId}/${component_name}`;
    } else if (component_name) {
      newId = component_name;
    }

    const event = {
      ...analytics.event,
      GA_event_action:
        'page component viewable impression' as AnalyticsEventActionType,
      component_id: newId,
      component_type: component_type,
      component_name: component_name,
      component_placement:
        component_placement ?? analytics.event.component_placement,
      component_placement_desktop:
        component_placement_desktop ??
        analytics.event.component_placement_desktop,
      section_placement:
        component_type === 'Section' && component_placement
          ? component_placement
          : analytics.event.section_placement,
      amount_of_articles:
        amount_of_articles ?? analytics.event.amount_of_articles,
      column_index_mobile:
        component_type === 'GalleryColumn' && component_placement
          ? component_placement
          : analytics.event.column_index_mobile,
      column_index_desktop:
        component_type === 'GalleryColumn' && component_placement_desktop
          ? component_placement_desktop
          : analytics.event.column_index_desktop,
      column_number_of_links:
        column_number_of_links ?? analytics.event.column_number_of_links,
    };

    useContextProvider(AnalyticsContext, {
      ...analytics,
      event,
    });

    // TODO: can we get useVisibleTask to work without the ref and intersection observer?
    // useVisibleTask by itself wasn't working correctly
    // if there was no extra div, it was firing on page load
    // if there was an extra div, it fired twice
    const containerRef = useSignal<Element>();

    // eslint-disable-next-line qwik/no-use-visible-task
    useVisibleTask$(() => {
      if (containerRef.value && fireViewEvent) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // checking amount_of_articles is a number to avoid sending duplicate events
              if (entry.isIntersecting) {
                analytics.actions.sendEvent(event);
                observer.disconnect(); // Only fire once
              }
            });
          },
          { threshold: 0.1 }
        );

        observer.observe(containerRef.value);

        return () => observer.disconnect();
      }
    });

    return (
      <>
        <div
          id={newId.toLowerCase().replace(/[ /]/g, '_')}
          ref={containerRef}
          class='absolute'
        />
        <Slot />
      </>
    );
  }
);
